import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UserService } from '@tw/services';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Table, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CommonService } from 'src/shared/services/common.service';
import {
  TrackApprovalResponse,
  TrackerService,
} from 'src/shared/services/tracker.service';
import { RequestApprovalStatus } from '../track-utils';
interface requestApprovalTab {
  label: string;
  key: string;
  reqBadgeCount?: number;
  showBadge?: boolean;
}
interface Column {
  field: string;
  header: string;
  type: string;
  width: string;
}

@Component({
  selector: 'tw-req-timer-entry',
  templateUrl: './req-timer-entry.component.html',
  styleUrls: ['./req-timer-entry.component.scss'],
  imports: [
    TabsModule,
    TableModule,
    ButtonModule,
    FloatLabel,
    DatePicker,
    IconField,
    InputIcon,
    TooltipModule,
    FormsModule,
    CommonModule,
    InputTextModule,
    CardModule,
    BadgeModule,
    ButtonModule,
    ConfirmDialogModule,
    ToastModule,
  ],
})
export class ReqTimerEntryComponent implements OnInit {
  @ViewChild('reqApprovalTable') reqApprovalTable!: Table;
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private userService = inject(UserService);
  requestApprovalData: TrackApprovalResponse[] = [];
  requestApprovalDataFiltered: TrackApprovalResponse[] = [];
  selectedBulkApprovalData: TrackApprovalResponse[] = [];
  requestApprovalColumn: Column[] = [];
  reqApprovalTabs: requestApprovalTab[] = [];
  requestApprovalStatus = RequestApprovalStatus;
  shouldShowTooltip = false;
  reqApprovalMaxDate!: Date;
  reqApprovalMinDate!: Date;
  yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
  requestApprovalActiveTab = 0;
  dateRange: Date[] = [
    new Date(new Date().getFullYear(), new Date().getMonth(), 1), // first of month
    new Date(new Date().setDate(new Date().getDate() - 1)), // yesterday
  ];
  requestApprovalGlobalSearch = '';
  constructor(
    private commonService: CommonService,
    private trackerService: TrackerService,
  ) {
    this.reqApprovalTabs = [
      {
        label: 'Pending',
        key: 'pending',
      },
      { label: 'Submitted', key: 'submitted' },
      {
        label: 'Approved',
        key: 'approved',
      },
      {
        label: 'Rejected',
        key: 'rejected',
        showBadge: true,
        reqBadgeCount: 0,
      },
    ];
  }

  ngOnInit() {
    this.requestApproval(true);
  }
  get isBulkSubmitDisabled(): boolean {
    const allAreZeroTime = this.requestApprovalDataFiltered.every(
      (x) => x.totalTimeSpent === '00:00',
    );
    return allAreZeroTime;
  }

  initializeDates() {
    const now = new Date();
    this.reqApprovalMaxDate = new Date(now);
    this.reqApprovalMinDate = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate(),
    );
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - 7);
    this.dateRange = [startDate, now];
  }

  async requestApproval(resetDate = false) {
    if (resetDate) {
      this.initializeDates();
    }
    this.requestApprovalData = [];
    this.selectedBulkApprovalData = [];
    this.requestApprovalGlobalSearch = '';
    this.reqApprovalTable?.clear();
    this.reqApprovalTable?.filterGlobal('', 'contains');

    if (this.dateRange[0]?.toString() && this.dateRange[1]?.toString()) {
      const startDate = this.commonService['dateService'].format(
        new Date(this.dateRange[0]?.toString()),
        'YYYY/MM/DD',
      );
      const endDate = this.commonService['dateService'].format(
        new Date(this.dateRange[1]?.toString()),
        'YYYY/MM/DD',
      );
      const approvalData = await this.trackerService.getApprovalTrack(
        startDate,
        endDate,
        [
          this.requestApprovalStatus.Pending,
          this.requestApprovalStatus.Submitted,
          this.requestApprovalStatus.Approved,
          this.requestApprovalStatus.Rejected,
        ],
      );
      this.requestApprovalData = approvalData || [];
      this.reqApprovalTabs.find(
        (x) => x.label === RequestApprovalStatus.Rejected,
      )!.reqBadgeCount = this.requestApprovalData.filter(
        (x) => x.status === RequestApprovalStatus.Rejected,
      ).length;
      await this.reqApprovalOnTabChange(this.requestApprovalActiveTab);
    } else {
      this.commonService.showToast(
        'Error',
        'Please select a valid date range.',
      );
    }
  }
  async reqApprovalOnTabChange(event: any) {
    this.requestApprovalGlobalSearch = '';
    this.reqApprovalTable?.clear();
    this.reqApprovalTable?.filterGlobal('', 'contains');
    this.selectedBulkApprovalData = [];
    if (this.reqApprovalTabs[event].label === RequestApprovalStatus.Pending) {
      this.setTableColumns();
      this.requestApprovalDataFiltered = this.requestApprovalData.filter(
        (x) => x.status === RequestApprovalStatus.Pending,
      );
    } else if (
      this.reqApprovalTabs[event].label === RequestApprovalStatus.Submitted
    ) {
      this.setTableColumns();
      this.requestApprovalDataFiltered = this.requestApprovalData.filter(
        (x) => x.status === RequestApprovalStatus.Submitted,
      );
    } else if (
      this.reqApprovalTabs[event].label === RequestApprovalStatus.Approved
    ) {
      this.setTableColumns();
      this.requestApprovalDataFiltered = this.requestApprovalData.filter(
        (x) => x.status === RequestApprovalStatus.Approved,
      );
    } else if (
      this.reqApprovalTabs[event].label === RequestApprovalStatus.Rejected
    ) {
      this.setTableColumns();
      this.requestApprovalDataFiltered = this.requestApprovalData.filter(
        (x) => x.status === RequestApprovalStatus.Rejected,
      );
    }
  }
  reqApprovalClose() {
    this.requestApprovalData = [];
    this.requestApprovalActiveTab = 0;
  }
  async submitForApproval(event: Event, date?: string, isBulk = false) {
    const minHours = this.userService.userDetails()?.minHours || '08:30:00';
    const minMinutes = this.convertToMinutes(minHours);

    let isLessThanMin = false;

    if (isBulk) {
      isLessThanMin = this.selectedBulkApprovalData.some(
        (x) => this.convertToMinutes(x.totalTimeSpent) < minMinutes,
      );
    } else if (date) {
      const entry = this.requestApprovalDataFiltered.find(
        (x) => x.taskDate === date,
      );
      if (entry) {
        isLessThanMin =
          this.convertToMinutes(entry.totalTimeSpent) < minMinutes;
      }
    }

    const message = isLessThanMin
      ? 'You have tracked less than the allocated minimum required hours per day. Are you sure you want to submit?'
      : 'Please make sure the data for the day is fully verified before submitting to your Supervisor!';

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: message,
      header: 'Confirmation',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'No',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Yes',
        severity: 'primary',
      },
      accept: async () => {
        try {
          const isRejectedTab =
            this.reqApprovalTabs[this.requestApprovalActiveTab].label ===
            RequestApprovalStatus.Rejected;

          let body: any[] = [];
          this.selectedBulkApprovalData = this.selectedBulkApprovalData.filter(
            (x) => x.totalTimeSpent !== '00:00',
          );
          if (isBulk && this.selectedBulkApprovalData.length > 0) {
            body = this.selectedBulkApprovalData.map((item) => ({
              effectiveDate: item.taskDate || null,
              ...(isRejectedTab && {
                timeEntryApprovalId: item.timeEntryApprovalId || 0,
              }),
            }));

            this.selectedBulkApprovalData = [];
          } else if (date) {
            if (isRejectedTab) {
              body = this.requestApprovalDataFiltered
                .filter((item) => item.taskDate === date)
                .map((item) => ({
                  effectiveDate: item.taskDate || null,
                  ...(isRejectedTab && {
                    timeEntryApprovalId: item.timeEntryApprovalId || 0,
                  }),
                }));
            } else {
              body = [{ effectiveDate: date || null }];
            }
          }

          if (body.length > 0) {
            if (isRejectedTab) {
              await this.trackerService.reSubmitForApproval(body);
            } else {
              await this.trackerService.submitForApproval(body);
            }

            this.commonService.showToast(
              'Success',
              'Selected entries submitted for approval.',
            );
            await this.requestApproval();
          }
        } catch (error) {
          console.error('Error submitting for approval:', error);
        }
      },
      reject: () => {
        // this.messageService.add({
        //   severity: 'error',
        //   summary: 'Rejected',
        //   detail: 'You have rejected',
        // });
      },
    });
  }
  
  async pullBackSubmission(date?: string, isBulk = false) {
    try {
      let ids: number[] = [];
  
      this.selectedBulkApprovalData = this.selectedBulkApprovalData.filter(
        (x) => x.totalTimeSpent !== '00:00',
      );
  
      if (isBulk && this.selectedBulkApprovalData.length > 0) {
        ids = this.selectedBulkApprovalData.map(
          (item) => item.timeEntryApprovalId || 0,
        );
  
        this.selectedBulkApprovalData = [];
      } else if (date) {
        ids = this.requestApprovalDataFiltered
          .filter((item) => item.taskDate === date)
          .map((item) => item.timeEntryApprovalId || 0);
      }
  
      if (ids.length > 0) {
        await this.trackerService.pullBackSubmission(ids);
  
        this.commonService.showToast(
          'Success',
          'Selected entries pulled back successfully.',
        );
        await this.requestApproval();
      }
    } catch (error) {
      console.error('Error pulling back submission:', error);
    }
  }
  setTableColumns() {
    this.requestApprovalColumn = [];
    if (
      this.reqApprovalTabs[this.requestApprovalActiveTab].label ===
      RequestApprovalStatus.Pending
    ) {
      this.requestApprovalColumn = [
        { field: 'selection', header: '', width: '10%', type: 'selection' },
        { field: 'taskDate', header: 'Date', width: '50%', type: 'date' },
        {
          field: 'totalTimeSpent',
          header: 'Logged Hours',
          width: '50%',
          type: '',
        },
        { field: 'actions', header: 'Actions', width: '30%', type: 'actions' },
      ];
    } else if (
      this.reqApprovalTabs[this.requestApprovalActiveTab].label ===
      RequestApprovalStatus.Submitted
    ) {
      this.requestApprovalColumn = [
        { field: 'selection', header: '', width: '10%', type: 'selection' },
        { field: 'taskDate', header: 'Date', width: '50%', type: 'date' },
        {
          field: 'totalTimeSpent',
          header: 'Logged Hours',
          width: '50%',
          type: '',
        },
        {
          field: 'submittedOn',
          header: 'Submitted On',
          width: '50%',
          type: 'date',
        },
        { field: 'actions', header: 'Actions', width: '30%', type: 'actions' },
      ];
    } else if (
      this.reqApprovalTabs[this.requestApprovalActiveTab].label ===
      RequestApprovalStatus.Approved
    ) {
      this.requestApprovalColumn = [
        { field: 'taskDate', header: 'Date', width: '50%', type: 'date' },
        {
          field: 'totalTimeSpent',
          header: 'Logged Hours',
          width: '50%',
          type: '',
        },
        { field: 'comments', header: 'Comment', width: '50%', type: '' },
        {
          field: 'approvedByName',
          header: 'Approved By',
          width: '50%',
          type: '',
        },
        {
          field: 'responsesUpdatedOn',
          header: 'Approved On',
          width: '50%',
          type: 'date',
        },
      ];
    } else if (
      this.reqApprovalTabs[this.requestApprovalActiveTab].label ===
      RequestApprovalStatus.Rejected
    ) {
      this.requestApprovalColumn = [
        { field: 'selection', header: '', width: '10%', type: 'selection' },
        { field: 'taskDate', header: 'Date', width: '50%', type: 'date' },
        {
          field: 'totalTimeSpent',
          header: 'Logged Hours',
          width: '50%',
          type: '',
        },
        { field: 'comments', header: 'Comment', width: '50%', type: '' },
        {
          field: 'approvedByName',
          header: 'Rejected By',
          width: '50%',
          type: '',
        },
        {
          field: 'responsesUpdatedOn',
          header: 'Rejected On',
          width: '50%',
          type: 'date',
        },
        { field: 'actions', header: 'Actions', width: '30%', type: 'actions' },
      ];
    }
  }
  async clearFilters() {
    await this.requestApproval(true);
  }
  checkTooltipVisibility(el: HTMLElement) {
    this.shouldShowTooltip = el.scrollWidth > el.clientWidth;
  }
  private convertToMinutes(time: string): number {
    const parts = time.split(':').map(Number);
    return parts[0] * 60 + parts[1];
  }
}
